import { useFormikContext } from 'formik'
import isEqual from 'lodash/isEqual'
import uniq from 'lodash/uniq'
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react'

import ancillaryUtils from '@lib/ancillary'
import connectionUtils from '@lib/connection'
import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import { useCheckout } from '@stores/checkout'

interface SeatAncillaryContextValue {
  ancillaries: Ancillary.Item[]
  globalAncillaries: Ancillary.Item[]
  activeAncillaries: Ancillary.Item[]

  activateAncillary(ancillary: Ancillary.Item): void

  deactivateAncillary(ancillary: Ancillary.Item): void

  isAncillaryActive(ancillary: Ancillary.Item): boolean
}

export function useSeatAncillaryContextValue(ancillaries: Ancillary.Item[]): SeatAncillaryContextValue {
  const {
    values: { ancillaries: activeAncillaries },
    setFieldValue,
  } = useFormikContext<CheckoutFormData>()
  const [{ outbound, inbound }] = useCheckout()

  const globalAncillaries = useMemo(
    () =>
      ancillaries.filter(
        ancillary =>
          (!outbound ||
            ancillary.segments?.some(ancillarySegment =>
              outbound.segments.some(connectionSegment =>
                connectionUtils.isSameSegment(connectionSegment, ancillarySegment),
              ),
            )) &&
          (!inbound ||
            ancillary.segments?.some(ancillarySegment =>
              inbound.segments.some(connectionSegment =>
                connectionUtils.isSameSegment(connectionSegment, ancillarySegment),
              ),
            )),
      ),
    [ancillaries, inbound, outbound],
  )

  const activateAncillary = useCallback(
    (ancillary: Ancillary.Item) => {
      setFieldValue('ancillaries.SEAT', uniq([...activeAncillaries.SEAT, ancillary]))
    },
    [activeAncillaries, setFieldValue],
  )

  const deactivateAncillary = useCallback(
    (ancillary: Ancillary.Item) => {
      setFieldValue(
        'ancillaries.SEAT',
        activeAncillaries.SEAT.filter(activeAncillary => activeAncillary.id !== ancillary.id),
      )
    },
    [activeAncillaries, setFieldValue],
  )

  const isAncillaryActive = useCallback(
    (ancillary: Ancillary.Item): boolean => activeAncillaries.SEAT.some(current => current.id === ancillary.id),
    [activeAncillaries],
  )

  useEffect(() => {
    const mandatoryAncillaries = ancillaries.filter(ancillary => ancillaryUtils.isMandatory(ancillary))

    let updatedAncillaries = activeAncillaries.SEAT.filter(ancillary => ancillaries.includes(ancillary))
    updatedAncillaries = uniq([...updatedAncillaries, ...mandatoryAncillaries])

    if (!isEqual(updatedAncillaries, activeAncillaries.SEAT)) setFieldValue('ancillaries.SEAT', updatedAncillaries)
  }, [ancillaries, activeAncillaries.SEAT, setFieldValue])

  return {
    ancillaries,
    activeAncillaries: activeAncillaries.SEAT,
    activateAncillary,
    deactivateAncillary,
    isAncillaryActive,
    globalAncillaries,
  }
}

export const SeatAncillariesContext = createContext<SeatAncillaryContextValue>({
  ancillaries: [],
  globalAncillaries: [],
  activeAncillaries: [],
  activateAncillary() {},
  deactivateAncillary() {},
  isAncillaryActive: () => false,
})
export const useSeatAncillaryContext = () => useContext(SeatAncillariesContext)
